import { useMemo, useContext } from 'react';
import {
    Row, Col, FormGroup, ListGroup, ListGroupItem,
    Alert,
} from 'reactstrap';
import { useParams /* , useHistory */ } from 'react-router';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { faUserClock } from '@fortawesome/free-solid-svg-icons';
import { appendTimeZone } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { useTranslation } from '@s20/i18n';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm, /* FlexCenterRow, */ FlexColumnStart, FormLabel, Input,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
// import { CallOffButton } from '../callOff/CallOffButton';

const CrewTimesheet = () => {
    const { t } = useTranslation();
    const { timesheetId, employeeId } = useParams();
    // const history = useHistory();

    const context = useContext(CommonContext);

    const { data: timesheet, isLoading } = useQuery({
        queryKey: ['crewDetails', timesheetId, employeeId],
        queryFn: () => httpClient.js(ApiRoutes.timesheet.crewDetails(timesheetId, employeeId)),
    });

    // Employee call off is switched off for now. Uncomment to look up the assignment for the Call Off button again.
    // const jobAssignmentId = timesheet?.timesheetDetails?.[0]?.jobAssignmentId;
    //
    // const { data: assignment } = useQuery({
    //     queryKey: ['crewCallOffAssignment', jobAssignmentId],
    //     queryFn: () => httpClient.js(ApiRoutes.jobAssignment.byId(jobAssignmentId)),
    //     enabled: Boolean(jobAssignmentId),
    // });


    const totalHours = useMemo(() => {
        if (!timesheet || timesheet.length === 0 || !timesheet.timesheetDetails) {
            return 0;
        }

        return timesheet.timesheetDetails.reduce((total, detail) => {
            if (detail.isBreak && !detail.isPaidBreak) {
                return total;
            }

            return total + detail.value;
        }, 0);
    }, [timesheet]);

    if (isLoading) {
        return (
            <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                <CustomCircularProgress
                    color="secondary"
                    variant="indeterminate"
                    label={t('timesheet:gettingTimesheet')}
                />
            </div>
        );
    }

    if (isEmpty(timesheet)) {
        return <div>{t('timesheet:noTimesheetData')}</div>;
    }

    return (
        <AppPageForm
            formShown={context.formIsOpen}
            formId="employeeTimesheetForm"
            formHeadingIcon={faUserClock}
            formHeading={`${t('timesheet:crewTimesheet')} : ${timesheet.timesheetNumber || ''}`}
            formName="employeeTimesheetForm"
            viewOnly
            backUrl={AppNavPaths.EmployeeTimesheets}
            backLabel={t('timesheet:backToMyTimesheets')}
        >
            <Row className="self-dispatch-timesheet-content">
                <Col
                    xl="6"
                    lg="8"
                    md="10"
                    sm="12"
                    className="ml-auto mr-auto pt-2"
                >
                    <FormGroup>
                        <FormLabel label={t('timesheet:status')} />
                        <Alert
                            className="m-0 pt-1 pb-2 pl-3 pr-3 text-center d-flex flex-row align-items-start"
                            color="secondary"
                        >
                            <FlexColumnStart className="flex-fill">
                                {timesheet.submittedOn ? (
                                    <FlexColumnStart className="w-100">
                                        <span className="ml-2">
                                            {t('timesheet:submittedOn', {
                                                when: appendTimeZone(
                                                    moment.parseZone(timesheet.submittedOn).format('MM/DD/YYYY, h:mm A'),
                                                    timesheet.submittedOn,
                                                    timesheet.timeZone,
                                                ),
                                            })}
                                        </span>
                                        <small>
                                            {t('timesheet:byUser', { user: timesheet.timesheetDetails[0]?.employeeFullName })}
                                        </small>
                                    </FlexColumnStart>
                                ) : (
                                    <span className="ml-2">{t('timesheet:notYetSubmitted')}</span>
                                )}
                            </FlexColumnStart>
                        </Alert>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel label={t('timesheet:employee')} />
                        <span className="form-control-plaintext">
                            {timesheet.timesheetDetails[0]?.employeeFullName}
                        </span>
                    </FormGroup>

                    <Input
                        label={t('timesheet:date')}
                        readOnly
                        type="date"
                        value={moment(timesheet.timesheetDetails[0]?.date).format('YYYY-MM-DD')}
                    />

                    <FormGroup>
                        <FormLabel label={t('timesheet:timesheetDetails')} />
                        <ListGroup>
                            {timesheet.timesheetDetails.map((detail, index) => (
                                <ListGroupItem
                                    key={index}
                                    className="d-flex flex-row align-items-center"
                                >
                                    <div className="flex-fill text-muted">
                                        <strong>{detail.chargeTypeName}</strong>
                                        <p className="text-muted mb-0">
                                            {t('timesheet:notesLabel')}
                                            {': '}
                                            {detail.notes || t('timesheet:notAvailable')}
                                        </p>
                                    </div>
                                    <div className="text-muted flex-fill text-right">
                                        <strong>
                                            {t('timesheet:durationFormat', { hours: Math.floor(detail.value), minutes: Math.round((detail.value % 1) * 60) })}
                                        </strong>
                                        <p className="text-muted mb-0">
                                            {moment(detail.start, 'HH:mm').format('h:mm A')}
                                            {' - '}
                                            {moment(detail.end, 'HH:mm').format('h:mm A')}
                                        </p>
                                    </div>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </FormGroup>
                    <hr />
                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                        <FormLabel
                            className="mb-0 w-100"
                            htmlFor="totalDurationAll"
                            label={t('timesheet:total')}
                            required={false}
                        />
                        <div className="d-flex flex-row flex-nowrap align-items-center justify-content-start">
                            <span
                                id="totalDurationAll"
                                className="h3 font-weight-bold text-muted m-0"
                            >
                                {t('timesheet:durationFormat', { hours: Math.floor(totalHours), minutes: Math.round((totalHours % 1) * 60) })}
                            </span>
                        </div>
                    </FormGroup>
                    <hr />
                    <FormGroup>
                        <FormLabel label={t('timesheet:employeeSignature')} />
                        {timesheet.timesheetReviews[0].reviewSignature?.signatureData ? (
                            <div className="border p-4 text-center">
                                <img
                                    src={timesheet.timesheetReviews[0].reviewSignature.signatureData}
                                    alt={t('timesheet:employeeSignature')}
                                    style={{ maxWidth: '100%', height: 'auto' }}
                                />
                            </div>
                        ) : (
                            <p className="form-control-plaintext">{t('timesheet:noSignatureAvailable')}</p>
                        )}
                    </FormGroup>
                    {!!timesheet.timesheetNumber && (
                        <FormGroup>
                            <FormLabel
                                label={t('timesheet:timesheetNumber')}
                                required={false}
                            />
                            <span className="form-control form-control-plaintext">
                                {timesheet.timesheetNumber}
                            </span>
                        </FormGroup>
                    )}
                    {
                        // Employee call off is switched off for now. Uncomment to show the Call Off button again.
                        // {Boolean(assignment?.canCallOff) && (
                        //     <FlexCenterRow className="userActions p-1">
                        //         {/* A span, not a button: the view-only form is a disabled fieldset, which disables real buttons. */}
                        //         <CallOffButton
                        //             tag="span"
                        //             role="button"
                        //             jobAssignmentId={jobAssignmentId}
                        //             onSuccess={() => history.push(AppNavPaths.AssignmentCalendar)}
                        //         />
                        //     </FlexCenterRow>
                        // )}
                    }
                </Col>
            </Row>
        </AppPageForm>
    );
};

export default CrewTimesheet;
